import { apiFetch } from '@/lib/apiFetch'

/** The single ACT exercise type. Each question varies by focus, not by type. */
export const ACT_EXERCISE_TYPE = 'ACT'

/** Loose practice, one question at a time under a chosen focus. */
export const MODE_PRACTICE = 'PRACTICE'
/** A full timed section run against the real test's clock. */
export const MODE_TEST = 'TEST'

export const FOCUS_TIME = 'TIME'
export const FOCUS_CORRECTNESS = 'CORRECTNESS'
export const FOCUS_STRATEGY = 'STRATEGY'
/** Written by full-section runs; never picked from the focus list. */
export const FOCUS_PACE = 'PACE'

export const TAG_STRATEGY_PLANNED = 'STRATEGY_PLANNED'

export type ActFocusKey = string

export interface CatalogTag {
  key: string
  label: string
  emoji: string
}

/** What he calls before the clock starts. */
export interface CatalogFocus {
  key: ActFocusKey
  label: string
  emoji: string
  blurb: string
  question: string
  prompt: string
  planCategory?: string
}

/** A section he sits for, with the real thing's question count and clock. */
export interface CatalogSection {
  key: string
  label: string
  emoji: string
  questions: number
  minutes: number
  secondsPerQuestion: number
}

/** A rung on the way to test speed. */
export interface CatalogPaceLevel {
  key: string
  label: string
  multiplier: number
  blurb: string
}

export interface ActCatalog {
  focuses: CatalogFocus[]
  sections: CatalogSection[]
  paceLevels: CatalogPaceLevel[]
  categories: Record<string, CatalogTag[]>
}

export interface ActReflectionTag {
  id: number
  logId: number
  category: string
  tagKey: string
}

export interface ActQuestionLog {
  id: number
  sessionId: number
  focus: ActFocusKey
  mode: string | null
  questionIndex: number
  section: string | null
  predictedSeconds: number | null
  targetSeconds: number | null
  actualSeconds: number | null
  predictedCorrect: boolean | null
  actualCorrect: boolean | null
  completedAt: number | null
  timeDeltaSeconds: number | null
  predictionHit: boolean | null
  onPace: boolean | null
  tags: ActReflectionTag[]
}

export interface ActSummary {
  questionsLogged: number
  questionsCompleted: number
  timedQuestions: number
  avgPredictedSeconds: number
  avgActualSeconds: number
  avgTimeErrorSeconds: number
  avgTimeBiasSeconds: number
  closeTimeGuesses: number
  underestimates: number
  overestimates: number
  timeAccuracy: number
  totalActualSeconds: number
  pacedQuestions: number
  onPaceQuestions: number
  avgTargetSeconds: number
  paceAccuracy: number
  gradedQuestions: number
  correctQuestions: number
  accuracy: number
  predictionQuestions: number
  predictionHits: number
  predictionAccuracy: number
  confidentAndRight: number
  confidentButWrong: number
  doubtedButRight: number
  doubtedAndWrong: number
  focusHits: number
  focusAccuracy: number
  bestFocusStreak: number
  currentFocusStreak: number
}

/** How a single strategy actually performs once the reps are in. */
export interface StrategyStat {
  tagKey: string
  label: string
  used: number
  graded: number
  correct: number
  accuracy: number
  avgSeconds: number
}

/** Real pace against a section's clock, plus the rung to work at next. */
export interface SectionPace {
  section: string
  label: string
  questions: number
  minutes: number
  testSecondsPerQuestion: number
  sampleQuestions: number
  avgSecondsPerQuestion: number | null
  recommendedPaceLevel: string
}

export interface ActSessionReview {
  session: { id: number; startedAt: number; endedAt: number | null; exerciseType: string | null }
  logs: ActQuestionLog[]
  summary: ActSummary
  strategies: StrategyStat[]
  bySection: { section: string; label: string; summary: ActSummary }[]
}

export interface ActInsights {
  childId: number
  days: number
  summary: ActSummary
  byFocus: Record<string, { label: string; summary: ActSummary }>
  bySection: { section: string; label: string; summary: ActSummary }[]
  strategies: StrategyStat[]
  pace: SectionPace[]
  daily: {
    date: string
    questions: number
    avgTimeErrorSeconds: number
    avgTimeBiasSeconds: number
    predictionAccuracy: number
    focusAccuracy: number
    accuracy: number
  }[]
  trend: { earlier: ActSummary; later: ActSummary; timeErrorImproved: boolean; predictionImproved: boolean } | null
  dayStreak: number
}

/** Same tolerance the backend scores against: the larger of 25% or 20 seconds. */
export function timeGuessWasClose(predicted: number | null, actual: number | null): boolean {
  if (predicted == null || actual == null) return false
  const tolerance = Math.max(20, Math.round(predicted * 0.25))
  return Math.abs(actual - predicted) <= tolerance
}

/**
 * Did he nail what this question asked him to call? Mirrors the backend, since
 * the streak counter has to update before the server round-trip lands.
 */
export function focusHit(log: ActQuestionLog): boolean {
  if (log.completedAt == null) return false
  switch (log.focus) {
    case FOCUS_TIME: return timeGuessWasClose(log.predictedSeconds, log.actualSeconds)
    case FOCUS_CORRECTNESS: return log.predictionHit === true
    case FOCUS_STRATEGY: return log.actualCorrect === true
    case FOCUS_PACE: return log.onPace === true
    default: return false
  }
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s}s`
  const mins = Math.floor(s / 60)
  const rem = s % 60
  return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`
}

export function formatClock(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(seconds ?? 0))
  const mins = Math.floor(s / 60)
  const rem = s % 60
  return `${mins}:${rem < 10 ? '0' : ''}${rem}`
}

export function formatDelta(delta: number | null | undefined): string {
  if (delta == null) return '—'
  if (delta === 0) return 'dead on'
  return delta > 0 ? `${formatDuration(delta)} over` : `${formatDuration(Math.abs(delta))} under`
}

export function percent(value: number | null | undefined): string {
  if (value == null) return '—'
  return `${Math.round(value * 100)}%`
}

export function focusOf(catalog: ActCatalog | null, key: ActFocusKey): CatalogFocus | null {
  return catalog?.focuses?.find(f => f.key === key) || null
}

export function sectionOf(catalog: ActCatalog | null, key: string | null): CatalogSection | null {
  if (!key) return null
  return catalog?.sections?.find(s => s.key === key) || null
}

export function paceLevelOf(catalog: ActCatalog | null, key: string | null): CatalogPaceLevel | null {
  if (!key) return null
  return catalog?.paceLevels?.find(p => p.key === key) || null
}

/** Seconds a question gets at this rung of the ladder. */
export function paceBudget(section: CatalogSection | null, level: CatalogPaceLevel | null): number {
  if (!section) return 0
  return Math.round(section.secondsPerQuestion * (level?.multiplier ?? 1))
}

/** Whole-section clock at this rung, in seconds. */
export function sectionBudget(section: CatalogSection | null, level: CatalogPaceLevel | null): number {
  if (!section) return 0
  return paceBudget(section, level) * section.questions
}

export function strategiesFor(log: ActQuestionLog): string[] {
  return (log.tags || []).filter(t => t.category === TAG_STRATEGY_PLANNED).map(t => t.tagKey)
}

export function tagLabel(catalog: ActCatalog | null, category: string, tagKey: string): string {
  const found = catalog?.categories?.[category]?.find(t => t.key === tagKey)
  return found ? found.label : tagKey
}

export function fetchCatalog(): Promise<ActCatalog> {
  return apiFetch<ActCatalog>('/act/catalog')
}

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  } catch {
    return 'America/New_York'
  }
}

/**
 * Magnus's one line on the question just finished. Short on purpose -- it rides
 * above the next question rather than stopping the round to hold a seminar.
 */
export function verdictLine(log: ActQuestionLog): string {
  const right = log.actualCorrect === true
  const took = formatDuration(log.actualSeconds)

  if (log.focus === FOCUS_TIME) {
    const delta = log.timeDeltaSeconds ?? 0
    if (timeGuessWasClose(log.predictedSeconds, log.actualSeconds)) {
      return delta === 0
        ? `Dead on at ${took}, and you ${right ? 'got it' : 'missed it'}. You know what these cost you.`
        : `${took} — inside your guess by ${formatDuration(Math.abs(delta))}. Good read.`
    }
    return delta > 0
      ? `${took}. That ate ${formatDuration(delta)} more than you called.`
      : `${took}. Faster than you called by ${formatDuration(Math.abs(delta))}.`
  }

  if (log.focus === FOCUS_CORRECTNESS) {
    if (log.predictionHit) {
      return right
        ? `Called it and got it, in ${took}. Trust that read.`
        : `You called the miss before you started — ${took} spent. That's an accurate read.`
    }
    return right
      ? `You doubted it and got it anyway, ${took}. Give yourself more credit.`
      : `You were sure and it slipped, ${took}. Those are the ones worth a second look.`
  }

  if (log.focus === FOCUS_PACE) {
    if (log.onPace) return `${took} — under your ${formatDuration(log.targetSeconds)} budget. Keep moving.`
    return `${took} against a ${formatDuration(log.targetSeconds)} budget. Bank it back on the next one.`
  }

  return right ? `Landed it in ${took}.` : `Missed it in ${took}.`
}
